import { useState, useEffect } from "react";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const ottawaCountyCities = [
  {
    name: "Holland",
    route: "/cities/holland",
    tagline: "Ottawa County's largest city — expedited passport services on Lake Michigan's eastern shore near the iconic Big Red lighthouse",
    highlights: ["Lake Michigan shoreline", "Serving Zeeland, Hudsonville, Saugatuck"],
    population: "34,000",
    badge: "Hub City",
  },
  {
    name: "Grand Haven",
    route: "/cities/grand-haven",
    tagline: "Ottawa County's coastal jewel — expedited passport services at the mouth of the Grand River near the famous red lighthouse",
    highlights: ["Grand River harbor", "Serving Spring Lake, Ferrysburg, Coopersville"],
    population: "11,500",
    badge: null,
  },
  {
    name: "Muskegon",
    route: "/cities/muskegon",
    tagline: "Muskegon County's coast — expedited passport services on Lake Michigan with Lake Express ferry access",
    highlights: ["Pere Marquette Beach", "Serving Norton Shores, Roosevelt Park, Whitehall"],
    population: "38,000",
    badge: null,
  },
  {
    name: "Grand Rapids",
    route: "/cities/grand-rapids",
    tagline: "Kent County hub — the West Michigan metro anchor connecting the entire lakeshore region to expedited passport services",
    highlights: ["Gerald R. Ford Int'l Airport", "Serving all of West Michigan"],
    population: "198,000",
    badge: null,
  },
];

const faqs = [
  {
    question: "Does Ottawa County have a passport agency?",
    answer:
      "Ottawa County does not have its own regional passport agency. The nearest agency is the Detroit Passport Agency (211 W. Fort St, Suite 1300, Detroit, MI 48226), approximately 175–180 miles from Holland and Grand Haven. Fast Passport Center provides courier service to this agency for all Ottawa County residents, eliminating the need for you to travel to Detroit.",
  },
  {
    question: "How fast can I get a passport in Ottawa County?",
    answer:
      "With Fast Passport Center, Ottawa County residents can get same-day emergency service or 2–3 week expedited processing. Routine service through the post office takes 6–13 weeks plus mailing time. Our courier service handles the trip to the Detroit Passport Agency for you.",
  },
  {
    question: "Where can I get passport photos in Ottawa County?",
    answer:
      "Passport photos are available at Walgreens, CVS, and Rite Aid locations throughout Holland, Grand Haven, and Spring Lake. Many post offices also offer photo services. For best results, ensure photos meet the 2x2 inch specification with a white background.",
  },
  {
    question: "Do Ottawa County residents need a passport for Canada?",
    answer:
      "A valid passport is required for all air travel to Canada. For land border crossings, U.S. citizens can use a passport card, enhanced driver's license, or Trusted Traveler card. A passport book is recommended for maximum flexibility.",
  },
  {
    question: "What post offices accept passport applications in Ottawa County?",
    answer:
      "The Holland Main Post Office (61 E 8th St), Grand Haven Post Office (20 N Harbor Dr), Spring Lake Post Office (200 E Savidge St), and Zeeland Post Office (150 E Main Ave) all accept passport applications. Appointments are recommended, especially during peak summer travel season.",
  },
  {
    question: "Can I renew my passport by mail from Ottawa County?",
    answer:
      "Yes, if your most recent passport is undamaged, was issued when you were 16 or older, and was issued within the last 15 years, you can renew by mail. Fast Passport Center can review your renewal application for completeness and expedite the processing for faster turnaround.",
  },
  {
    question: "What if I lost my passport in Ottawa County?",
    answer:
      "For lost or stolen passports, you must report it using Form DS-64 and apply in person using Form DS-11 at an acceptance facility. If you have urgent travel, Fast Passport Center can help you get a replacement quickly through our emergency service.",
  },
  {
    question: "How much does expedited passport service cost in Ottawa County?",
    answer:
      "Government fees are $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center offers service packages that include document review, photo verification, courier service to the Detroit Passport Agency, and tracking. Contact us for a quote based on your timeline.",
  },
];

export default function OttawaCountyPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title =
      "Expedited Passport Services Ottawa County MI | Holland, Grand Haven, Muskegon | Lake Michigan Coast | Fast Passport Center";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Fast Passport Center provides emergency and expedited passport services across Ottawa County, Michigan. Serving Holland, Grand Haven, Muskegon, and all Lake Michigan coast communities. Courier service to the Detroit Passport Agency."
      );
    }

    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute(
        "content",
        "expedited passport Ottawa County, emergency passport Holland MI, same day passport Grand Haven, fast passport Muskegon, passport renewal West Michigan coast, Ottawa County passport service, Lake Michigan coast passport"
      );
    }

    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) geoRegion.setAttribute("content", "US-MI");
    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) geoPlacename.setAttribute("content", "Ottawa County, Michigan");
    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute("content", "42.9523;-86.1090");
    const icbm = document.querySelector('meta[name="ICBM"]');
    if (icbm) icbm.setAttribute("content", "42.9523, -86.1090");

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute("href", "https://fastpassportcenter.com/ottawa-county");

    let schema = document.getElementById("ottawa-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "ottawa-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Michigan", item: "https://fastpassportcenter.com/michigan" },
          { "@type": "ListItem", position: 3, name: "Ottawa County", item: "https://fastpassportcenter.com/ottawa-county" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Fast Passport Center - Ottawa County",
        description:
          "Expedited and emergency passport services across Ottawa County, Michigan — Holland, Grand Haven, Muskegon, and the Lake Michigan coast.",
        url: "https://fastpassportcenter.com/ottawa-county",
        telephone: "+1-877-253-0084",
        email: "info@fastpassportcenter.com",
        priceRange: "$$",
        areaServed: {
          "@type": "County",
          name: "Ottawa County",
          containedInPlace: { "@type": "State", name: "Michigan" },
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "253",
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "20:00",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Emergency Passport Service Ottawa County",
        description:
          "Same-day and next-day emergency passport processing for Ottawa County travelers with urgent international travel needs.",
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: {
          "@type": "County",
          name: "Ottawa County",
          containedInPlace: { "@type": "State", name: "Michigan" },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-[#1A365D] overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Lake%20Michigan%20coastline%20in%20West%20Michigan%20with%20sandy%20beaches%2C%20scenic%20shoreline%2C%20lighthouses%2C%20green%20coastal%20landscape%2C%20clear%20blue%20water%2C%20beautiful%20summer%20day%2C%20no%20text%20no%20people%2C%20epic%20aerial%20nature%20photography&width=1400&height=600&seq=ottawa-hero-bg-001&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A365D]/60 via-[#1A365D]/50 to-[#1A365D]/70" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <nav className="text-sm text-white/60 mb-6">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <span className="mx-2">/</span>
              <a href="/states/michigan" className="hover:text-white transition-colors">Michigan</a>
              <span className="mx-2">/</span>
              <span className="text-white/80">Ottawa County</span>
            </nav>
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">
              Lake Michigan Coast Coverage
            </p>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Expedited Passport Services in Ottawa County, Michigan
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Emergency and expedited passport processing for all of Ottawa County and the Lake Michigan coast.
              Serving <strong className="text-white">Holland, Grand Haven, Muskegon,</strong> and <strong className="text-white">Grand Rapids</strong> with same-day and 2–3 week service options.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:8772530084"
                className="inline-flex items-center gap-2 bg-[#F6C344] text-[#1A365D] font-semibold px-6 py-3 rounded-lg hover:bg-white transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Call 877-253-0084
              </a>
              <a
                href="/cities/holland"
                className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
              >
                <i className="ri-map-pin-line"></i>
                Holland Hub
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Coastal Context Banner */}
      <section className="bg-[#F6C344]/10 border-b border-[#F6C344]/30 py-5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-[#1A365D]">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-anchor-line text-[#F6C344]"></i>
              </div>
              <span className="font-semibold">Lake Michigan Shoreline</span>
            </div>
            <div className="flex items-center gap-2 text-[#1A365D]">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-line text-[#F6C344]"></i>
              </div>
              <span className="font-semibold">Big Red + Grand Haven Lighthouses</span>
            </div>
            <div className="flex items-center gap-2 text-[#1A365D]">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-flight-takeoff-line text-[#F6C344]"></i>
              </div>
              <span className="font-semibold">Gerald R. Ford Int'l Airport (GRR)</span>
            </div>
            <div className="flex items-center gap-2 text-[#1A365D]">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-ship-line text-[#F6C344]"></i>
              </div>
              <span className="font-semibold">Lake Express Ferry — Muskegon to Milwaukee</span>
            </div>
          </div>
        </div>
      </section>

      {/* City Cards Grid */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              4 Cities Served
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Choose Your West Michigan Coastal City
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              Every community on the Lake Michigan coast is covered through our Ottawa County cluster.
              Select your city to view local acceptance facilities, processing times, and expedited services.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ottawaCountyCities.map((city, i) => (
              <a
                key={i}
                href={city.route}
                className="group relative bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
              >
                {city.badge && (
                  <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                    {city.badge}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className="ri-map-pin-2-line text-[#1A365D] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-lg">{city.name}</h3>
                    <p className="text-gray-400 text-xs">Population: {city.population}</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{city.tagline}</p>
                <ul className="space-y-2 mb-6">
                  {city.highlights.map((h, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm group-hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">
                  View Services{" "}
                  <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve the Lake Michigan Coast
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From Holland's Tulip Time tourists to Grand Haven's Coast Guard City families, from Muskegon's
              Lake Express ferry riders to the entire West Michigan shoreline — we help Lake Michigan coast
              travelers get their passports fast.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For Ottawa County travelers with flights within 72 hours. We hand-carry your application to the Detroit Passport Agency — no 175-mile road trip required.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–3 Week Service",
                desc: "Standard expedited processing with overnight shipping and tracking for Holland, Grand Haven, Muskegon, and all Ottawa County residents.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants on the Lake Michigan coast — we handle document prep, photos, and acceptance facility scheduling at your local post office.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "Ottawa County renewals by mail or courier. We ensure your application is complete to avoid delays before your next trip.",
              },
              {
                icon: "ri-ship-line",
                title: "Lake Express Ferry & Cruise Travel",
                desc: "Muskegon's Lake Express ferry to Milwaukee and Great Lakes cruises require a passport for some sailings. We help West Michigan coast residents stay travel-ready.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for Ottawa County residents who need a new passport quickly — especially for urgent summer beach travel or Coast Guard family transfers.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-[#F7F8FA] rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Processing Options */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Processing Speeds
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Passport Processing Options on the West Michigan Coast
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              Compare your options for getting a passport fast in Ottawa County.
              We recommend our courier service for urgent travel needs.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
              <thead>
                <tr className="bg-[#1A365D]">
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Service Type</th>
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Processing Time</th>
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Best For</th>
                  <th className="text-left text-white font-semibold text-sm px-6 py-4">Cost</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: "Emergency Same-Day", time: "24–72 hours", best: "Flights within 3 days", cost: "Gov fee + $60 expedite + service", highlight: true },
                  { service: "Expedited Courier", time: "2–3 weeks", best: "Upcoming travel in 1–2 months", cost: "Gov fee + $60 expedite + service", highlight: false },
                  { service: "Routine Post Office", time: "6–13 weeks", best: "No rush, budget option", cost: "Gov fee only", highlight: false },
                  { service: "Passport Renewal by Mail", time: "6–13 weeks (routine) / 2–3 weeks (expedited)", best: "Adult renewals with valid old passport", cost: "$130 book / $30 card + optional $60 expedite", highlight: false },
                ].map((row, i) => (
                  <tr key={i} className={`border-t border-gray-100 ${row.highlight ? "bg-[#F6C344]/5" : ""}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#1A365D] text-sm">{row.service}</span>
                        {row.highlight && (
                          <span className="bg-[#F6C344] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.time}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.best}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Local Area Info */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                Local Area
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">
                Ottawa County Passport Resources
              </h2>
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                <p>
                  <strong className="text-[#1A365D]">Nearest Passport Agency:</strong>{" "}
                  Detroit Passport Agency — 211 W. Fort St, Suite 1300, Detroit, MI 48226.
                  Approximately 175–180 miles from Holland and Grand Haven via I-196 and I-96.
                  Appointments required for same-day service.
                </p>
                <p>
                  <strong className="text-[#1A365D]">Local Airports:</strong>{" "}
                  Gerald R. Ford International Airport (GRR) is the primary airport for Ottawa County,
                  located in Grand Rapids approximately 30 miles northeast. It serves the entire West Michigan coast
                  with growing domestic and international routes. Detroit Metro Airport (DTW) is the major
                  international hub approximately 175 miles east.
                </p>
                <p>
                  <strong className="text-[#1A365D]">Acceptance Facilities:</strong>{" "}
                  Post offices in Holland, Grand Haven, Spring Lake, and Zeeland
                  accept passport applications. Appointments are recommended, especially during peak summer beach season.
                </p>
                <p>
                  <strong className="text-[#1A365D]">Lake Express Ferry:</strong>{" "}
                  Muskegon's Lake Express high-speed ferry to Milwaukee, Wisconsin runs seasonally.
                  Passengers may need a passport for some Great Lakes cruises and ferry sailings.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Holland", "Grand Haven", "Muskegon", "Grand Rapids"].map((city) => (
                  <a
                    key={city}
                    href={`/${city.toLowerCase().replace(/ /g, "-")}`}
                    className="inline-flex items-center text-xs font-medium text-[#1A365D] bg-[#EEF2F8] rounded-full px-3 py-1.5 hover:bg-[#1A365D] hover:text-white transition-all whitespace-nowrap"
                  >
                    {city}
                  </a>
                ))}
              </div>
            </div>
            <div className="bg-[#F7F8FA] rounded-lg p-8">
              <h3 className="font-bold text-[#1A365D] text-lg mb-4">
                Why Ottawa County Trusts Us
              </h3>
              <ul className="space-y-4">
                {[
                  {
                    icon: "ri-shield-check-line",
                    title: "Trusted Courier Service",
                    desc: "We hand-carry your application to the Detroit Passport Agency — no need for you to make the 175-mile trip.",
                  },
                  {
                    icon: "ri-map-pin-line",
                    title: "West Michigan Coast Knowledge",
                    desc: "We know Holland, Grand Haven, and Muskegon post offices and their scheduling quirks during peak summer season.",
                  },
                  {
                    icon: "ri-customer-service-2-line",
                    title: "Personal Support",
                    desc: "Talk to a real person who understands Ottawa County logistics and can guide you step by step.",
                  },
                  {
                    icon: "ri-speed-line",
                    title: "Fast Turnaround",
                    desc: "Same-day emergency service and 2–3 week expedited options for all Lake Michigan coast residents.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#1A365D] text-sm`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A365D] text-sm">{item.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Common Questions
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Ottawa County Passport FAQs
            </h2>
            <p className="text-gray-500 text-base leading-relaxed">
              Answers to the most common questions about getting a passport fast on the Lake Michigan coast.
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer"
                >
                  <span className="font-semibold text-[#1A365D] text-sm">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-[#1A365D] transition-transform flex-shrink-0 ${openFaq === i ? "rotate-180" : ""}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Your Passport Fast?
          </h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Whether you're in Holland, Grand Haven, Muskegon, or anywhere on the Lake Michigan coast —
            we make getting your passport fast and stress-free. Call now or visit your city's page.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="tel:8772530084"
              className="inline-flex items-center gap-2 bg-[#F6C344] text-[#1A365D] font-semibold px-8 py-4 rounded-lg hover:bg-white transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call 877-253-0084
            </a>
            <a
              href="/cities/holland"
              className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
            >
              <i className="ri-map-pin-line"></i>
              Holland Hub
            </a>
          </div>
          <p className="text-white/40 text-sm mt-6">
            Serving all of Ottawa County &amp; the Lake Michigan coast — 7 days a week
          </p>
        </div>
      </section>

      {/* SEO Cross Links */}
      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="MI" showCities={false} />
        </div>
      </section>
    </div>
  );
}